import React, { useState, useEffect } from 'react';
import { Upload, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { apiClient } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function SEOEditor({ 
  metaTitle = '', 
  metaDescription = '', 
  slug = '', 
  ogImage = '',
  defaultTitle = '',
  defaultDescription = '',
  onChange,
  isProductContext = false,
  pagePath = null
}) {
  const [data, setData] = useState({
    meta_title: metaTitle || defaultTitle,
    meta_description: metaDescription || defaultDescription,
    slug: slug || generateSlug(defaultTitle),
    og_image: ogImage
  });
  const [uploadingOG, setUploadingOG] = useState(false);

  // Generate URL-friendly slug
  function generateSlug(text) {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Update parent component when data changes
  useEffect(() => {
    onChange(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Auto-generate slug when defaultTitle changes (on first load or title change)
  useEffect(() => {
    if (defaultTitle && !slug) {
      setData(prev => ({ ...prev, slug: generateSlug(defaultTitle) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTitle]);

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlugChange = (value) => {
    // Clean slug in real-time
    const cleanSlug = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-');
    handleChange('slug', cleanSlug);
  };

  const handleOGImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    setUploadingOG(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data: uploadData } = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = uploadData.url.startsWith('http') 
        ? uploadData.url 
        : `${process.env.REACT_APP_BACKEND_URL}${uploadData.url}`;
      
      handleChange('og_image', imageUrl);
      toast.success('Image OG uploadée !');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error('OG Image upload error:', error);
    } finally {
      setUploadingOG(false);
    }
  };

  // Character count helpers
  const getTitleStatus = (length) => {
    if (length === 0) return { color: 'text-gray-400', icon: AlertCircle, message: 'Vide' };
    if (length < 30) return { color: 'text-orange-500', icon: AlertTriangle, message: 'Trop court' };
    if (length >= 30 && length <= 60) return { color: 'text-green-500', icon: CheckCircle, message: 'Optimal' };
    if (length > 60 && length <= 70) return { color: 'text-orange-500', icon: AlertTriangle, message: 'Un peu long' };
    return { color: 'text-red-500', icon: AlertCircle, message: 'Trop long' };
  };

  const getDescStatus = (length) => {
    if (length === 0) return { color: 'text-gray-400', icon: AlertCircle, message: 'Vide' };
    if (length < 70) return { color: 'text-orange-500', icon: AlertTriangle, message: 'Trop court' };
    if (length >= 70 && length <= 155) return { color: 'text-green-500', icon: CheckCircle, message: 'Optimal' };
    if (length > 155 && length <= 170) return { color: 'text-orange-500', icon: AlertTriangle, message: 'Un peu long' };
    return { color: 'text-red-500', icon: AlertCircle, message: 'Trop long' };
  };

  const titleStatus = getTitleStatus(data.meta_title.length);
  const descStatus = getDescStatus(data.meta_description.length);

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          <strong>Configuration SEO :</strong> Optimisez votre référencement naturel sur Google et l'apparence lors des partages sur les réseaux sociaux.
        </p>
      </div>

      {/* Meta Title */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
          Meta Title (Titre SEO)
        </label>
        <input
          type="text"
          value={data.meta_title}
          onChange={(e) => handleChange('meta_title', e.target.value)}
          placeholder="Titre optimisé pour Google (60 caractères max)"
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
        />
        <div className="flex items-center justify-between mt-2">
          <div className={`flex items-center gap-1.5 text-xs font-medium ${titleStatus.color}`}>
            <titleStatus.icon size={14} />
            <span>{titleStatus.message}</span>
          </div>
          <span className={`text-xs font-mono font-bold ${titleStatus.color}`}>
            {data.meta_title.length}/60
          </span>
        </div>
      </div>

      {/* Meta Description */}
      {isProductContext ? (
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
            Meta Description
          </label>
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <p className="text-xs text-blue-700 font-medium mb-1.5">
              🔗 La meta description reprend automatiquement la description du produit
            </p>
            <p className="text-sm text-gray-700 italic">
              {defaultDescription ? `"${defaultDescription.substring(0, 200)}${defaultDescription.length > 200 ? '...' : ''}"` : 'Renseignez la description dans l\'onglet Détails.'}
            </p>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
            Meta Description
          </label>
          <textarea
            value={data.meta_description}
            onChange={(e) => handleChange('meta_description', e.target.value)}
            placeholder="Description optimisée pour Google (155 caractères max)"
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <div className={`flex items-center gap-1.5 text-xs font-medium ${descStatus.color}`}>
              <descStatus.icon size={14} />
              <span>{descStatus.message}</span>
            </div>
            <span className={`text-xs font-mono font-bold ${descStatus.color}`}>
              {data.meta_description.length}/155
            </span>
          </div>
        </div>
      )}

      {/* Slug (URL) */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
          {isProductContext ? 'Slug (URL personnalisée)' : 'URL de la page'}
        </label>
        {isProductContext ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">joeycyan.com/produit/</span>
            <input
              type="text"
              value={data.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="mon-produit-personnalise"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-200">
            <span className="text-sm text-gray-600 font-mono">
              joeycyan.com{pagePath || '/'}
            </span>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-1.5">
          {isProductContext 
            ? 'Utilisez uniquement des lettres minuscules, chiffres et tirets (-)'
            : 'L\'URL de cette page est fixe et ne peut pas être modifiée'
          }
        </p>
      </div>

      {/* OG Image */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
          Image Open Graph (Partage réseaux sociaux)
        </label>
        
        {data.og_image ? (
          <div className="relative group">
            <img 
              src={data.og_image} 
              alt="OG Preview" 
              className="w-full max-w-md h-48 object-cover rounded-xl border-2 border-gray-200"
            />
            <button
              type="button"
              onClick={() => handleChange('og_image', '')}
              className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
            >
              Supprimer
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-[#00aaff] transition-colors">
            <input
              type="file"
              accept="image/*"
              onChange={handleOGImageUpload}
              disabled={uploadingOG}
              className="hidden"
              id="og-image-upload"
            />
            <label 
              htmlFor="og-image-upload" 
              className="flex flex-col items-center gap-3 cursor-pointer"
            >
              {uploadingOG ? (
                <div className="w-12 h-12 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
                    <Upload size={24} className="text-[#00aaff]" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-900">
                      Cliquez pour uploader une image
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Recommandé : 1200x630px (ratio 1.91:1)
                    </p>
                  </div>
                </>
              )}
            </label>
          </div>
        )}
      </div>

      {/* Google Snippet Preview */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-3 uppercase">
          Aperçu Google (Prévisualisation)
        </label>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
              JC
            </div>
            <div>
              <p className="text-xs text-gray-500">joeycyan.com</p>
              <p className="text-[10px] text-gray-400">
                {isProductContext 
                  ? `https://joeycyan.com/produit/${data.slug || 'exemple'}`
                  : `https://joeycyan.com${pagePath || '/'}`
                }
              </p>
            </div>
          </div>
          <h3 className="text-blue-600 text-lg font-normal hover:underline cursor-pointer mb-1">
            {data.meta_title || defaultTitle || 'Titre du produit - Joey Cyan'}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            {data.meta_description || defaultDescription || 'Description du produit qui apparaîtra dans les résultats de recherche Google...'}
          </p>
        </div>
      </div>
    </div>
  );
}
